import { useCallback, useEffect, useRef, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { Box, Button, Notification, Spinner, Tab, Tabs, Text } from "grommet";
import { aafhByEmailAddress, aafhByStatus } from "../../graphql/queries";
import {
  grantAAAFoodHandlerAccess,
  revokeAAAFoodHandlerAccess,
  UpdateAAAFoodHandlerSession,
} from "../../graphql/mutations";
import { dedupeAaaFoodHandlerSessions } from "./aaaFoodHandlerSessions";

const PAGE_LIMIT = 300;
const ACCESS_TOKEN = "578f877f-205f-47c6-85e0-afe16172237c";
const GRANT_SUCCESS = "{message=Student access has been granted successfully}";
const REVOKE_SUCCESS = "{message=Student access has been revoked}";

function prepareSessions(items) {
  return dedupeAaaFoodHandlerSessions(items).filter(
    (session) => session?.SessionId
  );
}

async function fetchAllIndexPages(query, connectionName, variables) {
  const items = [];
  let nextToken = null;
  const seenTokens = new Set();

  do {
    const response = await API.graphql({
      query,
      variables: {
        ...variables,
        limit: PAGE_LIMIT,
        sortDirection: "DESC",
        nextToken,
      },
    });
    const page = response?.data?.[connectionName];
    if (Array.isArray(page?.items)) {
      items.push(...page.items.filter(Boolean));
    }
    const token = page?.nextToken || null;
    if (!token || seenTokens.has(token)) {
      nextToken = null;
    } else {
      seenTokens.add(token);
      nextToken = token;
    }
  } while (nextToken);

  return items;
}

function patchSession(list, session) {
  return list.map((item) =>
    item.SessionId === session.SessionId ? { ...item, ...session } : item
  );
}

function displayValue(value) {
  if (value == null || value === "") {
    return "Not provided";
  }
  return value;
}

function sessionDate(createdAt) {
  if (typeof createdAt !== "string" || !createdAt) {
    return "Not provided";
  }
  return createdAt.split("T")[0];
}

function examStatusLabel(examStatus) {
  if (examStatus === "UNLOCKED") {
    return "Unlocked";
  }
  if (examStatus === "LOCKED") {
    return "Locked";
  }
  return "Not set";
}

function SessionCard({
  session,
  actionsLocked,
  onEnable,
  onExamStatus,
  onFinished,
}) {
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState(null);
  const buttonsDisabled = actionsLocked || actionPending;

  async function unlockExam() {
    if (buttonsDisabled) {
      return;
    }
    setActionPending(true);
    setError(null);
    try {
      const data = await API.graphql(
        graphqlOperation(grantAAAFoodHandlerAccess, {
          body: {
            user_id: session.StudentId,
            course_id: session.ExamId,
            token: ACCESS_TOKEN,
          },
        })
      );
      await API.graphql({
        query: UpdateAAAFoodHandlerSession,
        variables: {
          input: {
            SessionId: session.SessionId,
            ExamStatus: "UNLOCKED",
          },
        },
      });
      if (data.data.grantFoodHandlerAccess === GRANT_SUCCESS) {
        onExamStatus({ ...session, ExamStatus: "UNLOCKED" });
      } else {
        setError(
          data.data.grantFoodHandlerAccess || "Unable to unlock this exam."
        );
      }
    } catch {
      setError("Something may have gone wrong. Please try again.");
    } finally {
      setActionPending(false);
    }
  }

  async function lockExam() {
    if (buttonsDisabled) {
      return;
    }
    setActionPending(true);
    setError(null);
    try {
      const data = await API.graphql(
        graphqlOperation(revokeAAAFoodHandlerAccess, {
          body: {
            user_id: session.StudentId,
            course_id: session.ExamId,
            token: ACCESS_TOKEN,
          },
        })
      );
      await API.graphql({
        query: UpdateAAAFoodHandlerSession,
        variables: {
          input: {
            SessionId: session.SessionId,
            ExamStatus: "LOCKED",
          },
        },
      });
      if (data.data.revokeFoodHandlerAccess === REVOKE_SUCCESS) {
        onExamStatus({ ...session, ExamStatus: "LOCKED" });
      } else {
        setError(
          data.data.revokeFoodHandlerAccess || "Unable to lock this exam."
        );
      }
    } catch {
      setError("Something may have gone wrong. Please try again.");
    } finally {
      setActionPending(false);
    }
  }

  async function finishExam() {
    if (buttonsDisabled) {
      return;
    }
    setActionPending(true);
    setError(null);
    try {
      const data = await API.graphql(
        graphqlOperation(revokeAAAFoodHandlerAccess, {
          body: {
            user_id: session.StudentId,
            course_id: session.ExamId,
            token: ACCESS_TOKEN,
          },
        })
      );
      await API.graphql({
        query: UpdateAAAFoodHandlerSession,
        variables: {
          input: {
            SessionId: session.SessionId,
            ExamStatus: "LOCKED",
            Status: "COMPLETED",
          },
        },
      });
      if (data.data.revokeFoodHandlerAccess === REVOKE_SUCCESS) {
        onFinished(session);
      } else {
        setError(
          data.data.revokeFoodHandlerAccess || "Unable to finish this exam."
        );
      }
    } catch {
      setError("Something may have gone wrong. Please try again.");
    } finally {
      setActionPending(false);
    }
  }

  return (
    <Box
      pad="small"
      gap="small"
      margin={{ bottom: "small" }}
      background={actionsLocked ? "light-3" : "light-1"}
      round="xsmall"
      border={{ color: actionsLocked ? "light-4" : "light-3" }}
    >
      <Box gap="xsmall" style={actionsLocked ? { opacity: 0.55 } : undefined}>
        <Text weight="bold">{displayValue(session.Name)}</Text>
        {session.Status === "COMPLETED" && (
          <Text size="small" weight="bold">
            Completed
          </Text>
        )}
        <Text size="small">Email: {displayValue(session.EmailAddress)}</Text>
        <Text size="small">
          Course language: {displayValue(session.CourseLanguage)}
        </Text>
        <Text size="small">Session date: {sessionDate(session.createdAt)}</Text>
        <Text size="small">
          Exam status: {examStatusLabel(session.ExamStatus)}
        </Text>
        <Box direction="row" gap="small" wrap margin={{ top: "xsmall" }}>
          <Button
            label="Unlock Exam"
            primary
            color="status-ok"
            disabled={buttonsDisabled}
            onClick={unlockExam}
            a11yTitle="Unlock exam"
          />
          <Button
            label="Lock Exam"
            primary
            color="status-critical"
            disabled={buttonsDisabled}
            onClick={lockExam}
            a11yTitle="Lock exam"
          />
          <Button
            label="Finish Exam"
            primary
            color="black"
            disabled={buttonsDisabled}
            onClick={finishExam}
            a11yTitle="Finish exam"
          />
        </Box>
      </Box>
      {actionsLocked && (
        <Button
          label="Enable"
          primary
          color="black"
          onClick={() => onEnable(session.SessionId)}
          a11yTitle="Enable lock and unlock"
        />
      )}
      {error && (
        <Notification
          status="critical"
          title={error}
          onClose={() => setError(null)}
        />
      )}
    </Box>
  );
}

function SessionList({
  sessions,
  status,
  error,
  emptyMessage,
  onRetry,
  actionsLockedFor,
  onEnable,
  onExamStatus,
  onFinished,
}) {
  if (status === "loading" || status === "idle") {
    return (
      <Box align="center" pad="medium" gap="small">
        <Spinner />
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box align="center" pad="medium" gap="small">
        <Text textAlign="center">{error}</Text>
        <Button label="Retry" onClick={onRetry} />
      </Box>
    );
  }

  if (sessions.length === 0) {
    return (
      <Box pad={{ vertical: "medium" }}>
        <Text>{emptyMessage}</Text>
      </Box>
    );
  }

  return (
    <Box margin={{ top: "small" }}>
      {sessions.map((session) => (
        <SessionCard
          key={session.SessionId}
          session={session}
          actionsLocked={actionsLockedFor(session)}
          onEnable={onEnable}
          onExamStatus={onExamStatus}
          onFinished={onFinished}
        />
      ))}
    </Box>
  );
}

export default function AAAFoodHandlersPanel({ testerEmail, onClose }) {
  const [activeTab, setActiveTab] = useState(0);
  const [matches, setMatches] = useState([]);
  const [matchesStatus, setMatchesStatus] = useState("loading");
  const [matchesError, setMatchesError] = useState(null);
  const [missingEmail, setMissingEmail] = useState(false);
  const [queue, setQueue] = useState([]);
  const [queueStatus, setQueueStatus] = useState("idle");
  const [queueError, setQueueError] = useState(null);
  const [enabledSessionIds, setEnabledSessionIds] = useState(() => new Set());

  const matchesLoadGenRef = useRef(0);
  const queueLoadGenRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      matchesLoadGenRef.current += 1;
      queueLoadGenRef.current += 1;
    };
  }, []);

  const loadMatches = useCallback(async () => {
    const loadId = ++matchesLoadGenRef.current;
    const email = typeof testerEmail === "string" ? testerEmail.trim() : "";
    setMatchesStatus("loading");
    setMatchesError(null);

    if (!email) {
      if (!isMountedRef.current || loadId !== matchesLoadGenRef.current) {
        return;
      }
      setMatches([]);
      setMissingEmail(true);
      setMatchesStatus("ready");
      return;
    }

    setMissingEmail(false);

    try {
      const items = await fetchAllIndexPages(
        aafhByEmailAddress,
        "aafhByEmailAddress",
        { EmailAddress: email }
      );
      if (!isMountedRef.current || loadId !== matchesLoadGenRef.current) {
        return;
      }
      setMatches(prepareSessions(items));
      setMatchesStatus("ready");
    } catch {
      if (!isMountedRef.current || loadId !== matchesLoadGenRef.current) {
        return;
      }
      setMatchesError("Unable to load matches. Try again.");
      setMatchesStatus("error");
    }
  }, [testerEmail]);

  const loadQueue = useCallback(async () => {
    const loadId = ++queueLoadGenRef.current;
    setQueueStatus("loading");
    setQueueError(null);

    try {
      const items = await fetchAllIndexPages(aafhByStatus, "aafhByStatus", {
        Status: "QUEUE",
      });
      if (!isMountedRef.current || loadId !== queueLoadGenRef.current) {
        return;
      }
      setQueue(prepareSessions(items));
      setQueueStatus("ready");
    } catch {
      if (!isMountedRef.current || loadId !== queueLoadGenRef.current) {
        return;
      }
      setQueueError("Unable to load the queue. Try again.");
      setQueueStatus("error");
    }
  }, []);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  useEffect(() => {
    if (activeTab !== 1) {
      return;
    }
    loadQueue();
  }, [activeTab, loadQueue]);

  const handleExamStatus = useCallback((session) => {
    setMatches((prev) => patchSession(prev, session));
    setQueue((prev) => patchSession(prev, session));
  }, []);

  const handleFinished = useCallback((session) => {
    const updated = {
      ...session,
      ExamStatus: "LOCKED",
      Status: "COMPLETED",
    };
    setMatches((prev) => patchSession(prev, updated));
    setQueue((prev) =>
      prev.filter((item) => item.SessionId !== session.SessionId)
    );
    setEnabledSessionIds((prev) => {
      if (!prev.has(session.SessionId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(session.SessionId);
      return next;
    });
  }, []);

  const handleEnable = useCallback((sessionId) => {
    setEnabledSessionIds((prev) => {
      const next = new Set(prev);
      next.add(sessionId);
      return next;
    });
  }, []);

  const matchesActionsLocked = useCallback(
    (session) =>
      session.Status === "COMPLETED" && !enabledSessionIds.has(session.SessionId),
    [enabledSessionIds]
  );

  return (
    <Box width="large" pad="medium" overflow="auto" fill="vertical">
      <Box
        direction="row"
        justify="between"
        align="center"
        margin={{ bottom: "small" }}
      >
        <Button
          icon={<span style={{ fontSize: "20px" }}>×</span>}
          onClick={onClose}
          plain
          hoverIndicator
          a11yTitle="Close"
        />
        <Text size="large" weight="bold" textAlign="center">
          AAA Food Handlers
        </Text>
        <Box width="40px" />
      </Box>
      <Text
        size="small"
        textAlign="center"
        margin={{ bottom: "small" }}
      >
        Click outside of the sidebar to close
      </Text>

      <Tabs activeIndex={activeTab} onActive={setActiveTab}>
        <Tab title="Matches">
          <Box pad={{ top: "medium" }}>
            {missingEmail ? (
              <Text>No tester email to match.</Text>
            ) : (
              <SessionList
                sessions={matches}
                status={matchesStatus}
                error={matchesError}
                emptyMessage="No matching sessions for this email."
                onRetry={loadMatches}
                actionsLockedFor={matchesActionsLocked}
                onEnable={handleEnable}
                onExamStatus={handleExamStatus}
                onFinished={handleFinished}
              />
            )}
          </Box>
        </Tab>
        <Tab title="Queue">
          <Box pad={{ top: "medium" }}>
            <SessionList
              sessions={queue}
              status={queueStatus}
              error={queueError}
              emptyMessage="No queued sessions."
              onRetry={loadQueue}
              actionsLockedFor={() => false}
              onEnable={handleEnable}
              onExamStatus={handleExamStatus}
              onFinished={handleFinished}
            />
          </Box>
        </Tab>
      </Tabs>
    </Box>
  );
}
